import { routes } from '@routes';
import { PmOnboardingStepKey } from '@gql';

/**
 * Onboarding step keys used throughout the PM onboarding flow.
 * Note: 'code_of_conduct' is an alias for 'conduct' (backend compatibility).
 */
export type OnboardingStepKey =
  | 'join'
  | 'import'
  | 'conduct'
  | 'code_of_conduct'
  | 'subscribe'
  | 'review';

/**
 * Human-readable labels for each onboarding step.
 */
export const STEP_LABELS: Record<OnboardingStepKey, string> = {
  join: 'Setup',
  import: 'Import',
  conduct: 'Standards',
  code_of_conduct: 'Standards',
  subscribe: 'Subscription',
  review: 'Review',
};

/**
 * Route paths for each onboarding step.
 */
export const STEP_ROUTES: Record<OnboardingStepKey, string> = {
  join: routes.onboardingJoin,
  import: routes.onboardingImportListings,
  conduct: routes.onboardingCodeOfConduct,
  code_of_conduct: routes.onboardingCodeOfConduct,
  subscribe: routes.onboardingSubscribe,
  review: routes.onboardingReview,
};

/**
 * Mapping from GraphQL onboarding step keys to app step keys.
 */
export const GQL_TO_STEP_KEY: Record<PmOnboardingStepKey, OnboardingStepKey> = {
  [PmOnboardingStepKey.Join]: 'join',
  [PmOnboardingStepKey.Import]: 'import',
  [PmOnboardingStepKey.CodeOfConduct]: 'conduct',
  [PmOnboardingStepKey.Subscribe]: 'subscribe',
  [PmOnboardingStepKey.Review]: 'review',
};

/**
 * Valid onboarding step keys for runtime validation.
 */
export const VALID_ONBOARDING_STEP_KEYS: readonly OnboardingStepKey[] = [
  'join',
  'import',
  'conduct',
  'code_of_conduct',
  'subscribe',
  'review',
];

/**
 * Type guard to check if a string is a valid onboarding step key.
 */
export function isValidOnboardingStepKey(
  value: string
): value is OnboardingStepKey {
  return VALID_ONBOARDING_STEP_KEYS.includes(value as OnboardingStepKey);
}

/**
 * Ordered list of onboarding steps for progression checks.
 * Note: Does not include 'code_of_conduct' alias as it maps to 'conduct'.
 */
export const STEP_ORDER: readonly OnboardingStepKey[] = [
  'join',
  'import',
  'conduct',
  'subscribe',
  'review',
];
